import {
	cloneElement,
	type HTMLAttributes,
	type ReactElement,
	type ReactNode
} from 'react'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogBody,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogTitle,
	DialogTrigger
} from '@/components/ui/dialog'
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger
} from '@/components/ui/drawer'
import { Spinner } from '@/components/ui/spinner'
import { useMinWidth } from '@/features/marquee/hooks/use-min-width'
import { useTranslation } from '@/translations/i18n'

/**
 * The element that opens the confirmation, wherever the caller keeps it inside
 * `ResponsiveConfirmDialog`. It resolves to the right primitive's trigger for
 * the current breakpoint, so a caller writes it once for both.
 *
 * It reads its context, not its DOM position, so it works anywhere under the
 * dialog — including inside a portalled menu, which is the case this exists
 * for: a Radix menu unmounts its content on select, so the item that opens a
 * dialog can only be a trigger when the dialog itself is mounted *around* the
 * menu rather than beside the item.
 */
export function ResponsiveConfirmTrigger({
	children
}: {
	children: ReactElement<HTMLAttributes<HTMLElement>>
}) {
	const isDesktop = useMinWidth('l')

	if (isDesktop) {
		return <DialogTrigger render={children} />
	}

	return (
		<DrawerTrigger asChild>
			{/* Stop the trigger holding focus while vaul applies aria-hidden
			    to the page wrapper (avoids the aria-hidden focus warning). */}
			{cloneElement(children, {
				onPointerDown: (e: React.PointerEvent) => e.preventDefault()
			})}
		</DrawerTrigger>
	)
}

interface ResponsiveConfirmDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	/**
	 * Everything the confirmation is mounted around, holding the
	 * `ResponsiveConfirmTrigger` that opens it. Usually just the action button;
	 * for a menu item it is the whole menu, so the dialog outlives the menu
	 * closing on select.
	 */
	children: ReactNode
	/** Glyph centred above the title (a warning, a check, an envelope). */
	icon?: ReactNode
	title: ReactNode
	description: ReactNode
	/**
	 * Blocks below the description and above the footer: a notice, a list. The
	 * description primitive is a paragraph, so anything that is not a sentence
	 * goes here, laid out left-aligned like the upload dialog's body.
	 */
	body?: ReactNode
	confirmLabel: ReactNode
	onConfirm: () => void
	/** Disables the confirm button and shows a spinner while the action runs. */
	isPending?: boolean
	/** Desktop only: the corner ✕ (the drawer is dismissed by dragging). */
	showCloseButton?: boolean
}

/**
 * Confirmation dialog shared by the marquee event actions (lock / start /
 * close / delete). Desktop (≥ l) renders a centered `Dialog`; mobile renders a
 * bottom `Drawer` (sheet). Controlled via `open` / `onOpenChange` so the caller
 * can close it once its mutation settles.
 */
export function ResponsiveConfirmDialog({
	open,
	onOpenChange,
	children,
	icon,
	title,
	description,
	body,
	confirmLabel,
	onConfirm,
	isPending,
	showCloseButton = false
}: ResponsiveConfirmDialogProps) {
	const { t } = useTranslation()
	const isDesktop = useMinWidth('l')

	if (isDesktop) {
		return (
			<Dialog onOpenChange={onOpenChange} open={open}>
				{children}
				<DialogContent
					className='max-w-120'
					onClick={(clickEvent) => clickEvent.stopPropagation()}
					showCloseButton={showCloseButton}
				>
					<DialogBody className='items-center gap-2 text-center'>
						{icon && <div className='pb-2'>{icon}</div>}
						<DialogTitle>{title}</DialogTitle>
						<DialogDescription className='text-neutral-500'>
							{description}
						</DialogDescription>
						{body && (
							<div className='flex w-full flex-col gap-4 pt-4 text-left'>
								{body}
							</div>
						)}
					</DialogBody>
					<DialogFooter className='flex flex-row justify-between'>
						<DialogClose
							render={<Button type='button' variant='tertiary' />}
						>
							{t('marquee:cancel')}
						</DialogClose>
						<Button
							disabled={isPending}
							onClick={onConfirm}
							type='button'
							variant='primary'
						>
							{confirmLabel}
							{isPending && <Spinner size={16} />}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<Drawer onOpenChange={onOpenChange} open={open}>
			{children}
			<DrawerContent
				onClick={(clickEvent) => clickEvent.stopPropagation()}
			>
				<DrawerHeader className='pb-2'>
					{icon && (
						<div className='flex justify-center pb-2'>{icon}</div>
					)}
					<DrawerTitle className='font-semibold text-neutral-900 text-xl'>
						{title}
					</DrawerTitle>
					<DrawerDescription className='text-neutral-500'>
						{description}
					</DrawerDescription>
				</DrawerHeader>
				{body && (
					<div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4'>
						{body}
					</div>
				)}
				<DrawerFooter className='flex-row justify-between'>
					<DrawerClose asChild>
						<Button type='button' variant='tertiary'>
							{t('marquee:cancel')}
						</Button>
					</DrawerClose>
					<Button
						disabled={isPending}
						onClick={onConfirm}
						type='button'
						variant='primary'
					>
						{confirmLabel}
						{isPending && <Spinner size={16} />}
					</Button>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	)
}
